import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart3,
  TrendingUp,
  Target,
  Download,
  Calendar
} from 'lucide-react';
import { PerformanceMetrics } from '@/components/admin/analytics/PerformanceMetrics';
import { PlayerComparison } from '@/components/admin/analytics/PlayerComparison';
import { SessionTrends } from '@/components/admin/analytics/SessionTrends';
import { TechnicalAnalysis } from '@/components/admin/analytics/TechnicalAnalysis';
import { ExportDialog } from '@/components/admin/analytics/ExportDialog';
import { useAuth } from '@/hooks/useAuth';

export function AnalyticsDashboard() {
  const { user, organization } = useAuth();
  const [timeRange, setTimeRange] = useState('7d');
  const [_selectedPlayer, _setSelectedPlayer] = useState('all');
  const [showExportDialog, setShowExportDialog] = useState(false);

  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['analytics', organization?.id, timeRange, _selectedPlayer],
    queryFn: async () => {
      // Mock analytics data
      return {
        overview: {
          totalSessions: 45,
          totalShots: 1250,
          avgSessionDuration: 42,
          improvementRate: 15.2,
          activeUsers: 18,
          completionRate: 87
        },
        trends: {
          sessions: [
            { date: '2024-01-12', value: 8 },
            { date: '2024-01-13', value: 12 },
            { date: '2024-01-14', value: 6 },
            { date: '2024-01-15', value: 15 },
            { date: '2024-01-16', value: 9 },
            { date: '2024-01-17', value: 11 },
            { date: '2024-01-18', value: 13 }
          ],
          performance: [
            { date: '2024-01-12', quality: 7.2, accuracy: 68 },
            { date: '2024-01-13', quality: 7.8, accuracy: 72 },
            { date: '2024-01-14', quality: 7.5, accuracy: 70 },
            { date: '2024-01-15', quality: 8.1, accuracy: 75 },
            { date: '2024-01-16', quality: 7.9, accuracy: 73 },
            { date: '2024-01-17', quality: 8.3, accuracy: 78 },
            { date: '2024-01-18', quality: 8.6, accuracy: 81 }
          ]
        },
        topPerformers: [
          { name: 'Alex Johnson', sessions: 12, avgQuality: 9.1, improvement: 18 },
          { name: 'Sarah Chen', sessions: 10, avgQuality: 8.8, improvement: 14 },
          { name: 'Mike Wilson', sessions: 15, avgQuality: 8.5, improvement: 22 },
          { name: 'Emma Davis', sessions: 8, avgQuality: 8.3, improvement: 12 }
        ],
        technicalMetrics: {
          shotTypes: {
            'Drive': 35,
            'Pull': 22,
            'Cut': 18,
            'Sweep': 15,
            'Hook': 10
          },
          qualityDistribution: {
            'Excellent': 28,
            'Good': 42,
            'Average': 23,
            'Poor': 7
          }
        }
      };
    },
    enabled: !!organization?.id
  });

  const timeRangeOptions = [
    { value: '1d', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 3 months' }
  ];

  const isAdmin = user?.role === 'ADMIN';

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 bg-gray-200 rounded animate-pulse" />
        <div className="grid gap-4 md:grid-cols-4">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
        <div className="h-64 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            {isAdmin 
              ? `Comprehensive performance analytics for ${organization?.name}`
              : 'Monitor your team\'s performance and progress'
            }
          </p>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[160px]">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {timeRangeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" onClick={() => setShowExportDialog(true)}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData?.overview.totalSessions}</div>
            <p className="text-xs text-muted-foreground">
              +{Math.round((analyticsData?.overview.totalSessions ?? 0) * 0.12)} from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Shots Analyzed</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData?.overview.totalShots?.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              +{Math.round((analyticsData?.overview.totalShots ?? 0) * 0.08)} from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Session</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData?.overview.avgSessionDuration}min</div>
            <p className="text-xs text-muted-foreground">
              +2min from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Improvement Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analyticsData?.overview.improvementRate}%</div>
            <p className="text-xs text-green-600">
              +{((analyticsData?.overview.improvementRate ?? 0) - 12.1).toFixed(1)}% improvement
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Tabs */}
      <Tabs defaultValue="performance" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="players">Players</TabsTrigger>
          <TabsTrigger value="sessions">Sessions</TabsTrigger>
          <TabsTrigger value="technical">Technical</TabsTrigger>
        </TabsList>

        <TabsContent value="performance">
          <PerformanceMetrics data={analyticsData} timeRange={timeRange} />
        </TabsContent>

        <TabsContent value="players">
          <PlayerComparison 
            players={analyticsData?.topPerformers || []} 
            timeRange={timeRange}
          />
        </TabsContent>

        <TabsContent value="sessions">
          <SessionTrends
            data={analyticsData?.trends ?? null}
            timeRange={timeRange}
          />
        </TabsContent>

        <TabsContent value="technical">
          <TechnicalAnalysis 
            data={analyticsData?.technicalMetrics} 
            timeRange={timeRange}
          />
        </TabsContent>
      </Tabs>

      {/* Export Dialog */}
      <ExportDialog 
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        data={analyticsData}
        timeRange={timeRange}
      />
    </div>
  );
}